import React, { useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';

import MediaLightboxModal from './MediaLightboxModal';
import { cloudinaryCore, isCloudinaryMedia, isCloudinaryMediaUrl } from '../../util/cloudinary';
import customAnalytics from '../../util/custom-analytics';
import { MUTATION, QUERY } from '../../gql-operations';
import {
    getUniqueMediaSources,
    getUrlMetaDirectS3VideoSource,
    getUrlMetaOriginalVideoUrl,
    getUrlMetaVideoSource,
    resolveUrlMetaPreviewMedia
} from '../../util/url-meta-media';

const getPreferredLightboxSources = object => {
    if (!object) {
        return [];
    }

    return getUniqueMediaSources([
        object.thumbnail?.original,
        object.thumbnail?.xl,
        object.thumbnail?.lg,
        object.cdn_url,
        object.url,
        object.image,
        object.author_image_original,
        object.author_image_cdn_url,
        object.author_image
    ]);
};

const toLightboxView = object => {
    if (!object) {
        return null;
    }

    const unavailableItem = {
        src: '',
        unavailable: true,
        unavailableLabel: 'Preview unavailable'
    };

    if (object.src) {
        const sourceChain = getUniqueMediaSources([
            object.originalSrc,
            object.src,
            ...(Array.isArray(object.sourceChain) ? object.sourceChain : [])
        ]);
        return {
            src: sourceChain[0] || object.src,
            fallbackUrl: sourceChain[1] || null,
            sourceChain,
            fileName: object.fileName || object.displayName || object.name || null,
            ocrText: object.media?.ocrText || object.ocrText || null
        };
    }

    const videoSource = getUrlMetaVideoSource(object);
    if (videoSource && String(object.media_type || object.type || '').toLowerCase() === 'video') {
        const sourceChain = getUniqueMediaSources([
            videoSource,
            object.media_hls_url,
            getUrlMetaOriginalVideoUrl(object),
            object.media_cdn_url
        ]);
        const posterSources = getUniqueMediaSources([
            object.image_cdn_url,
            object.image
        ]);

        return {
            type: 'video',
            src: sourceChain[0] || videoSource,
            hlsUrl: getUrlMetaDirectS3VideoSource(object) ? null : object.media_hls_url || null,
            sourceChain,
            poster: posterSources[0] || null,
            fileName: object.displayName || object.name || object.title || null,
            ocrText: object.media?.ocrText || object.ocrText || null
        };
    }

    if (object.format === 'heic' && object.public_id && isCloudinaryMedia(object)) {
        const url = cloudinaryCore.url(object.public_id, {
            quality: 50,
            fetchFormat: 'auto',
            crop: 'fit',
            width: 1080
        });
        return {
            src: url,
            sourceChain: getUniqueMediaSources([url]),
            fileName: object.displayName || object.name || null
        };
    }

    if (
        object.image ||
        object.image_cdn_url ||
        object.author_image ||
        object.author_image_cdn_url ||
        object.author_image_original
    ) {
        if (
            typeof object.image === 'string' ||
            object.image_cdn_url ||
            object.author_image ||
            object.author_image_cdn_url ||
            object.author_image_original
        ) {
            const previewMedia = resolveUrlMetaPreviewMedia(object);
            if (previewMedia.primarySource) {
                return {
                    src: previewMedia.primarySource,
                    fallbackUrl: previewMedia.fallbackSource,
                    sourceChain: previewMedia.sourceChain,
                    fileName: object.displayName || object.name || object.title || null,
                    ocrText: object.media?.ocrText || object.ocrText || null
                };
            }

            return unavailableItem;
        }

        const isCloudinaryImage = isCloudinaryMedia({
            public_id: object.public_id,
            url: object.image
        });

        if (isCloudinaryImage) {
            const sourceChain = getUniqueMediaSources([
                object.thumbnail?.original,
                object.url,
                object.image,
                cloudinaryCore.url(object.image)
            ]);
            return {
                src: sourceChain[0] || '',
                fallbackUrl: sourceChain[1] || null,
                sourceChain,
                fileName: object.displayName || object.name || null,
                ocrText: object.media?.ocrText || object.ocrText || null,
                unavailable: sourceChain.length === 0,
                unavailableLabel: 'Preview unavailable'
            };
        }

        const sourceChain = getPreferredLightboxSources(object);
        return {
            src: sourceChain[0] || '',
            fallbackUrl: sourceChain[1] || null,
            sourceChain,
            ocrText: object.media?.ocrText || object.ocrText || null,
            unavailable: sourceChain.length === 0,
            unavailableLabel: 'Preview unavailable'
        };
    }

    if (object.url) {
        const looksLikeUrlMeta = Boolean(
            object.publisher ||
            object.title ||
            object.description ||
            object.logo ||
            object.author ||
            object.author_handle
        );

        if (looksLikeUrlMeta) {
            return unavailableItem;
        }

        const isCloudinaryUrl = isCloudinaryMediaUrl(object.url) || isCloudinaryMedia(object);

        if (isCloudinaryUrl) {
            const sourceChain = getUniqueMediaSources([
                object.thumbnail?.original,
                object.url,
                cloudinaryCore.url(object.url)
            ]);
            return {
                src: sourceChain[0] || '',
                fallbackUrl: sourceChain[1] || null,
                sourceChain,
                fileName: object.displayName || object.name || null,
                ocrText: object.media?.ocrText || object.ocrText || null,
                unavailable: sourceChain.length === 0,
                unavailableLabel: 'Preview unavailable'
            };
        }

        const sourceChain = getPreferredLightboxSources(object);
        return {
            src: sourceChain[0] || '',
            fallbackUrl: sourceChain[1] || null,
            sourceChain,
            fileName: object.displayName || object.name || null,
            ocrText: object.media?.ocrText || object.ocrText || null,
            unavailable: sourceChain.length === 0,
            unavailableLabel: 'Preview unavailable'
        };
    }

    return unavailableItem;
};

function CarouselModal({
    isOpen,
    closeModal,
    items,
    currentMessage,
    fileType,
    messageId,
    initialIndex = 0,
    onExtractAllOcrCompleted,
    allOcrExtracted = false,
    readOnly = false,
    allowExtraction = true
}) {
    const extractedMessageId = messageId || currentMessage?.id;
    const [getMessage, { data: fetchedMessageData }] = useLazyQuery(QUERY.GET_MESSAGE, {
        fetchPolicy: 'network-only',
        onError: error => {
            if (process.env.NODE_ENV === 'development') {
                console.log('Failed to update lastViewedAt:', error);
            }
        }
    });
    const [extractCarouselOcr, { loading: extractingCarouselOcr }] = useMutation(
        MUTATION.EXTRACT_CAROUSEL_OCR
    );
    const fetchedMessage = fetchedMessageData?.getMessage;

    useEffect(() => {
        if (readOnly || !isOpen || !extractedMessageId) {
            return;
        }

        getMessage({
            variables: {
                trackView: true,
                data: {
                    id: extractedMessageId
                }
            }
        });
    }, [readOnly, extractedMessageId, getMessage, isOpen]);

    const effectiveItems = useMemo(() => {
        if (
            fileType === 'Link' &&
            Array.isArray(items) &&
            Array.isArray(fetchedMessage?.urlMeta) &&
            fetchedMessage.urlMeta.length > 0
        ) {
            return items.map(sourceItem => {
                const source = sourceItem || {};
                const fetchedMeta = fetchedMessage.urlMeta.find(meta =>
                    meta?.image_name && source?.image_name && meta.image_name === source.image_name
                ) || fetchedMessage.urlMeta.find(meta =>
                    meta?.url && source?.url && meta.url === source.url
                ) || fetchedMessage.urlMeta[0];

                if (!fetchedMeta) {
                    return source;
                }

                if (
                    Number.isInteger(source.gallery_item_index) &&
                    Array.isArray(fetchedMeta.gallery) &&
                    fetchedMeta.gallery[source.gallery_item_index]
                ) {
                    const fetchedSlide = fetchedMeta.gallery[source.gallery_item_index];
                    return {
                        ...source,
                        media: fetchedSlide.media || source.media || null,
                        ocrText: fetchedSlide.media?.ocrText || source.media?.ocrText || source.ocrText || null
                    };
                }

                return { ...source, ...fetchedMeta };
            });
        }

        return items;
    }, [fetchedMessage, fileType, items]);

    const views = useMemo(() => {
        if (!Array.isArray(effectiveItems) || effectiveItems.length === 0) {
            return [{ src: '', unavailable: true, unavailableLabel: 'Preview unavailable' }];
        }

        const mappedItems = effectiveItems.map(toLightboxView).filter(Boolean);
        return mappedItems.length > 0
            ? mappedItems
            : [{ src: '', unavailable: true, unavailableLabel: 'Preview unavailable' }];
    }, [effectiveItems]);

    const handleExtractAllOcr = async () => {
        if (readOnly || !allowExtraction || !extractedMessageId || extractingCarouselOcr) return;
        await extractCarouselOcr({
            variables: { data: { messageId: extractedMessageId } }
        });
        const refreshedResult = await getMessage({
            variables: { trackView: false, data: { id: extractedMessageId } }
        });
        const initialMessage = refreshedResult?.data?.getMessage || null;
        onExtractAllOcrCompleted?.(initialMessage);

        const pollForArticlePreview = async (attempt = 0) => {
            if (attempt >= 8) return;
            window.setTimeout(async () => {
                try {
                    const result = await getMessage({
                        variables: { trackView: false, data: { id: extractedMessageId } }
                    });
                    const updatedMessage = result?.data?.getMessage || null;
                    onExtractAllOcrCompleted?.(updatedMessage);
                    const hasArticleBody = updatedMessage?.urlMeta?.some(meta =>
                        String(meta?.article_body || '').trim()
                    );
                    if (!hasArticleBody) {
                        await pollForArticlePreview(attempt + 1);
                    }
                } catch (_error) {
                    await pollForArticlePreview(attempt + 1);
                }
            }, 2500);
        };

        await pollForArticlePreview();
    };

    return (
        <MediaLightboxModal
            isOpen={isOpen}
            onClose={event => {
                customAnalytics.message_action({
                    action: 'View Image',
                    type: fileType
                });
                closeModal?.(event);
            }}
            views={views}
            initialIndex={initialIndex}
            ocrText={fetchedMessage?.ocrText ?? currentMessage?.ocrText ?? null}
            onExtractAllOcr={!readOnly && allowExtraction && fileType === 'Link' && views.length > 1 ? handleExtractAllOcr : null}
            extractingAllOcr={extractingCarouselOcr}
            allOcrExtracted={allOcrExtracted}
            hideDownload={fileType === 'Link' && views.length > 1}
        />
    );
}

export default CarouselModal;
